<template>
    <tailwind-layout title="Rent Payments">
        <template #header>
            <div class="px-4 sm:px-6 lg:px-8 mx-auto hidden lg:flex justify-between items-center"> <!-- Block for lg and larger -->
                <div>
                    <h1 class="font-semibold text-2xl leading-tight">Rent Payments</h1>
                    <h2 class="text-2xl font-bold mt-5">{{ property.address_1 }}</h2>
                    <p class="text-sm font-medium underline">
                        <a :href="'https://www.google.com/maps?q='+property.map_address"
                        target="_blank">{{ property.full_address }} <font-awesome-icon :icon="faMapMarkerAlt" class="h-5 w-5" aria-hidden="true" /></a>
                    </p>
                </div>
                <div class="flex items-center space-x-4">
                    <inertia-link class="whiteButton" :href="$page.props.user.role === 'applicant' ? route('my-property', { tab: 'rent-payments' }) : route('properties.show', property.id) + '?tab=rent-payments'">
                        <FontAwesome :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                    </inertia-link>
                </div>
            </div>

            <div class="mb-3 px-6 lg:px-8 mx-auto flex flex-col lg:hidden"><!-- Block for sm, md, lg (buttons will stack on smaller screens) -->
                <div class="flex justify-between items-center">
                    <div>
                        <h1 class="font-semibold text-2xl leading-tight">Rent Payments</h1>
                        <h2 class="text-2xl font-bold mt-5">{{ property.address_1 }}</h2>
                        <p class="text-sm font-medium underline">
                            <a :href="'https://www.google.com/maps?q='+property.map_address"
                            target="_blank">{{ property.full_address }} <font-awesome-icon :icon="faMapMarkerAlt" class="h-5 w-5" aria-hidden="true" /></a>
                        </p>
                    </div>
                </div>
            </div>
        </template>
        <div v-if="$page.props.user.role === 'applicant'" v-for="eachTransaction in requires_action_transactions" class="rounded-md bg-yellow-50 p-4 mb-6">
          <div class="flex">
            <div class="flex-shrink-0">
              <font-awesome-icon icon="fa-info-circle" class="h-5 w-5 text-yellow-400" aria-hidden="true" />
            </div>
            <div class="ml-3 text-sm text-yellow-700">
              <div>
                One of your payments needs further verification to be processed. Please click the link below to complete the verification.
              </div>
              <div class="mt-2">
                <a target="_blank" :href="eachTransaction.hosted_verification_url" class="text-blue-600">
                  Go to verification page
                </a>
              </div>
            </div>

          </div>
        </div>
        <div class="overflow-hidden">
            <div class="flex mb-2 mt-3 items-center">
                <h2 class="font-bold uppercase ml-2 sm:ml-0">One Time Charges</h2>
                        <inertia-link v-if="parseFloat(oneTimeChargesTotal.remaining) > 0 && $page.props.user.role === 'applicant' && rented_property.status === 1"
                              title="Pay All"
                              :href="route('rented-properties.pay', [rented_property.id, 'one-time-charges', 'all'])"
                              class="primaryButton ml-auto">
                    Pay All (${{oneTimeChargesTotal.remaining}})
                </inertia-link>
            </div>
            <div class="rounded-md lg:shadow shadow-none overflow-x-auto">
                <div class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="shadow overflow-hidden lg:border">
                                <table class="min-w-full divide-y divide-gray-300 hidden lg:table">
                                    <thead class="bg-blue-50">
                                    <tr>
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Title
                                        </th>
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Amount
                                        </th>
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Paid
                                        </th>
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Remaining Amount
                                        </th>
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Due Date
                                        </th>
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="(one_time_charge, index) in one_time_charges" :key="one_time_charge.id" class="bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer">
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{one_time_charge.title}}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            ${{one_time_charge.amount}}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            ${{one_time_charge.paid}}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            ${{one_time_charge.remaining}}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{one_time_charge.due_date}}
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <inertia-link
                                                v-if="one_time_charge.is_pay_button && rented_property.status === 1"
                                                title="Pay"
                                                :href="route('rented-properties.pay', [rented_property.id, 'one-time-charges', one_time_charge.id])"
                                                class="text-indigo-600 hover:text-indigo-900 mr-2">
                                                Pay
                                            </inertia-link>
                                            <button
                                                v-if="$page.props.user.role === 'agent' && one_time_charge.title !== 'Total' && parseFloat(one_time_charge.remaining) > 0 && !isSharedViewOnly"
                                                @click="openManualOneTimeChargeModal(one_time_charge)"
                                                title="Mark as paid"
                                                class="text-indigo-600 hover:text-indigo-900 mr-2">
                                                Mark as paid
                                            </button>
                                            <button
                                                v-if="one_time_charge.title === 'Total'"
                                                @click="showOneTimeChargesPayments()"
                                                title="Payments"
                                                class="text-indigo-600 hover:text-indigo-900">
                                                Detail
                                            </button>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>

                                <!-- Stacked layout for sm, md, and lg (table becomes vertical) -->
                                <ul role="list" class="lg:hidden grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 my-5 px-2">
                                    <li v-for="(one_time_charge, index) in one_time_charges" :key="one_time_charge.id" class="bg-white hover:outline-blue-500 hover:bg-blue-50 divide-gray-200 col-span-1 divide-y rounded-lg shadow-lg hover:outline hover:outline-offset-2 border">
                                        <div class="flex flex-col p-6">
                                            <h3 class="truncate text-sm font-medium text-gray-900">{{ one_time_charge.title }}</h3>
                                            <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Amount:</span> ${{ one_time_charge.amount }}</h4>
                                            <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Paid:</span> ${{ one_time_charge.paid }}</h4>
                                            <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Remaining:</span> ${{ one_time_charge.remaining }}</h4>
                                            <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Due Date:</span> {{ one_time_charge.due_date }}</h4>
                                        </div>
                                        <div>
                                            <div class="divide-gray-200 -mt-px flex divide-x">
                                                <div class="flex w-0 flex-1 flex-wrap">
                                                <inertia-link
                                                    v-if="one_time_charge.is_pay_button && rented_property.status === 1"
                                                        title="Pay"
                                                        :href="route('rented-properties.pay', [rented_property.id, 'one-time-charges', one_time_charge.id])"
                                                        class="hover:bg-blue-200 relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900">
                                                        <font-awesome-icon icon="fa-dollar-sign" />
                                                        Pay
                                                    </inertia-link>
                                                    <button
                                                        v-if="$page.props.user.role === 'agent' && one_time_charge.title !== 'Total' && parseFloat(one_time_charge.remaining) > 0 && !isSharedViewOnly"
                                                        @click="openManualOneTimeChargeModal(one_time_charge)"
                                                        title="Mark as paid"
                                                        class="hover:bg-blue-200 relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 border border-transparent py-4 text-sm font-semibold text-gray-900">
                                                        <font-awesome-icon icon="fa-check" />
                                                        Mark as paid
                                                    </button>
                                                    <div v-if="one_time_charge.title === 'Total'" class="-ml-px flex w-0 flex-1">
                                                        <button
                                                            v-if="one_time_charge.title === 'Total'"
                                                            @click="showOneTimeChargesPayments()"
                                                            title="Payments"
                                                            class="hover:bg-blue-200 relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-900">
                                                            <font-awesome-icon icon="fa-eyes" />
                                                            Detail
                                                        </button>
                                                    </div>
                                                    <div v-if="one_time_charge.title === 'Total'" class="-ml-px flex w-0 flex-1">
                                                        <inertia-link v-if="parseFloat(oneTimeChargesTotal.remaining) > 0 && $page.props.user.role === 'applicant' && rented_property.status === 1"
                                                                    title="Pay All"
                                                                    :href="route('rented-properties.pay', [rented_property.id, 'one-time-charges', 'all'])"
                                                                    class="hover:bg-blue-200 relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-900"
                                                                >
                                                                <font-awesome-icon icon="fa-dollar-sign" />
                                                                Pay All (${{oneTimeChargesTotal.remaining}})
                                                        </inertia-link>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                </ul>

                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="relative">
                <div class="grid grid-cols-2 mb-2 mt-4 sm:flex sm:justify-items-stretch">
                    <h2 class="font-bold uppercase mb-2 mt-4">Monthly Rental Ledger</h2>
                    <div class="sm:w-2/12 pr-4 order-2 sm:order-1 sm:ml-auto">
                        <select v-model="form.paginate"
                                class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-100 drop-shadow focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                                aria-label="Number of items per page">
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                </div>

            </div>
            <div class="rounded-md lg:shadow shadow-none overflow-x-auto">
                <div class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="shadow overflow-hidden lg:border">
                                <table class="min-w-full divide-y divide-gray-300 hidden lg:table">
                                    <thead class="bg-blue-50">
                                    <tr>
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Month
                                        </th>
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Rent
                                        </th>
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Late Fees
                                        </th>
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Total
                                        </th>
<!--                                        <th scope="col"-->
<!--                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">-->
<!--                                            Paid-->
<!--                                        </th>-->
<!--                                        <th scope="col"-->
<!--                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">-->
<!--                                            Extra Charges-->
<!--                                        </th>-->
<!--                                        <th scope="col"-->
<!--                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">-->
<!--                                            {{$page.props.user.role === 'applicant' ? 'Stripe Card/ACH Fees' : 'Stripe Fee'}}-->
<!--                                        </th>-->
<!--                                        <th scope="col"-->
<!--                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">-->
<!--                                            {{$page.props.user.role === 'applicant' ? 'Processing Fee' : 'Convenience Fee'}}-->
<!--                                        </th>-->
<!--                                        <th v-if="$page.props.user.role === 'agent'" scope="col"-->
<!--                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">-->
<!--                                            Monthly Account Fee-->
<!--                                        </th>-->
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Total Paid
                                            <span v-if="$page.props.user.role === 'agent'" class="block text-[10px] font-normal">By Tenant</span>
                                        </th>
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Remaining Amount
                                            <span class="block text-[10px] font-normal">Exculding Late Fees</span>
                                        </th>
<!--                                        <th scope="col"-->
<!--                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">-->
<!--                                            Last Payment Date-->
<!--                                        </th>-->
<!--                                        <th scope="col"-->
<!--                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">-->
<!--                                            Last Payment Status-->
<!--                                        </th>-->
<!--                                        <th scope="col"-->
<!--                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">-->
<!--                                            Last Payment Method-->
<!--                                        </th>-->
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="(payment, index) in payments.data" :key="index" :class="payment.month_name === 'Total' ? 'bg-blue-50 border-b' : 'bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer'">
                                        <td :class="payment.month_name === 'Total' ? 'whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-gray-900' : 'whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900'">
                                            {{ payment.month_name }}
                                        </td>
                                        <td :class="payment.month_name === 'Total' ? 'whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-gray-900' : 'whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900'">
                                            ${{payment.rent}}
                                        </td>
                                        <td :class="payment.month_name === 'Total' ? 'whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-gray-900' : 'whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900'">
                                            <span>${{payment.extra_charges}}</span>
                                            <button v-if="$page.props.user.role === 'agent' && payment.is_pay_button && !isSharedViewOnly" @click="editExtraCharges(payment, index)" class="ml-3"><font-awesome-icon icon="fa-edit" /></button>
                                        </td>
                                        <td :class="payment.month_name === 'Total' ? 'whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-gray-900' : 'whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900'">
                                            <span>${{payment.total}}</span>
                                        </td>
<!--                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">-->
<!--                                            <span>${{payment.sub_total_paid}}</span>-->
<!--                                        </td>-->
<!--                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">-->
<!--                                            <span>${{payment.extra_charges_paid}}</span>-->
<!--                                        </td>-->
<!--                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">-->
<!--                                            <span>${{$page.props.user.role === 'agent' ? payment.landlord_stripe_fee : payment.tenant_stripe_fee}}</span>-->
<!--                                        </td>-->
<!--                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">-->
<!--                                            <span>${{payment.processing_convenience_fee}}</span>-->
<!--                                        </td>-->
<!--                                        <td v-if="$page.props.user.role === 'agent'" class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">-->
<!--                                            <span>${{payment.monthly_account_fee}}</span>-->
<!--                                        </td>-->
                                        <td :class="payment.month_name === 'Total' ? 'whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-gray-900' : 'whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900'">
                                            <span>${{payment.total_paid}}</span>
                                        </td>
                                        <td :class="payment.month_name === 'Total' ? 'whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-gray-900' : 'whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900'">
                                            <span>${{payment.remaining}}</span>
                                        </td>
<!--                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">-->
<!--                                            <span>{{payment.last_payment_date}}</span>-->
<!--                                        </td>-->
<!--                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">-->
<!--                                            <span>{{payment.last_payment_status}}</span>-->
<!--                                        </td>-->
<!--                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">-->
<!--                                            <span>{{payment.last_payment_type}}</span>-->
<!--                                        </td>-->
                                        <td :class="payment.month_name === 'Total' ? 'px-6 py-4 whitespace-nowrap text-right text-sm font-semibold' : 'px-6 py-4 whitespace-nowrap text-right text-sm font-medium'">
                                            <inertia-link
                                                v-if="payment.is_pay_button && $page.props.user.role === 'applicant' && rented_property.status === 1"
                                                title="Pay"
                                                :href="route('rented-properties.pay', [rented_property.id, 'monthly-rent', index])"
                                                class="text-indigo-600 hover:text-indigo-900 mr-2">
                                                Pay
                                            </inertia-link>
                                            <button
                                                v-if="$page.props.user.role === 'agent' && parseFloat(payment.remaining) > 0 && payment.month_name !== 'Total' && !isSharedViewOnly"
                                                @click="openManualPaymentModal(payment, index)"
                                                title="Mark as paid"
                                                class="text-indigo-600 hover:text-indigo-900 mr-2">
                                                Mark as paid
                                            </button>
                                            <button
                                                v-if="payment?.payment_count"
                                                @click="showMonthPayments(payment)"
                                                title="Month Payments Detail"
                                                class="text-indigo-600 hover:text-indigo-900">
                                                Detail
                                            </button>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>

                                <!-- Stacked layout for sm, md, and lg (table becomes vertical) -->
                                <ul role="list" class="lg:hidden grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 my-5 px-2">
                                    <li v-for="(payment, index) in payments.data" :key="index" :class="payment.month_name === 'Total' ? 'bg-blue-50 divide-gray-200 col-span-1 divide-y rounded-lg shadow-lg border' : 'bg-white hover:outline-blue-500 hover:bg-blue-50 divide-gray-200 col-span-1 divide-y rounded-lg shadow-lg hover:outline hover:outline-offset-2 border'">
                                        <div class="flex flex-col p-6">
                                            <h3 :class="payment.month_name === 'Total' ? 'truncate text-sm font-semibold text-gray-900' : 'truncate text-sm font-medium text-gray-900'">{{ payment.month_name }}</h3>
                                            <h4 :class="payment.month_name === 'Total' ? 'mt-1 truncate text-sm font-semibold text-gray-900' : 'mt-1 truncate text-sm text-gray-500'"><span :class="payment.month_name === 'Total' ? 'font-semibold' : 'text-gray-900'">Rent:</span> ${{ payment.rent }}</h4>
                                            <h4 :class="payment.month_name === 'Total' ? 'mt-1 truncate text-sm font-semibold text-gray-900' : 'mt-1 truncate text-sm text-gray-500'"><span :class="payment.month_name === 'Total' ? 'font-semibold' : 'text-gray-900'">Late Fees:</span> ${{ payment.extra_charges }}</h4>
                                            <h4 :class="payment.month_name === 'Total' ? 'mt-1 truncate text-sm font-semibold text-gray-900' : 'mt-1 truncate text-sm text-gray-500'"><span :class="payment.month_name === 'Total' ? 'font-semibold' : 'text-gray-900'">Total:</span> ${{ payment.total }}</h4>
                                            <h4 :class="payment.month_name === 'Total' ? 'mt-1 truncate text-sm font-semibold text-gray-900' : 'mt-1 truncate text-sm text-gray-500'"><span :class="payment.month_name === 'Total' ? 'font-semibold' : 'text-gray-900'">Total Paid:</span> ${{ payment.total_paid }}</h4>
                                            <h4 :class="payment.month_name === 'Total' ? 'mt-1 truncate text-sm font-semibold text-gray-900' : 'mt-1 truncate text-sm text-gray-500'"><span :class="payment.month_name === 'Total' ? 'font-semibold' : 'text-gray-900'">Remaining Amount:</span> ${{ payment.remaining }}</h4>
                                        </div>
                                        <div>
                                            <div class="divide-gray-200 -mt-px flex divide-x flex-wrap">
                                                <div class="flex w-0 flex-1 flex-wrap">
                                                    <div v-if="payment?.payment_count" class="-ml-px flex w-0 flex-1">
                                                        <button
                                                            @click="showMonthPayments(payment)"
                                                            title="Month Payments Detail"
                                                            class="hover:bg-blue-200 relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 border border-transparent py-4 text-sm font-semibold text-gray-900">
                                                            <font-awesome-icon icon="fa-eyes" />
                                                            Detail
                                                        </button>
                                                    </div>
                                                    <button
                                                        v-if="$page.props.user.role === 'agent' && parseFloat(payment.remaining) > 0 && payment.month_name !== 'Total' && !isSharedViewOnly"
                                                        @click="openManualPaymentModal(payment, index)"
                                                        title="Mark as paid"
                                                        class="hover:bg-blue-200 relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 border border-transparent py-4 text-sm font-semibold text-gray-900">
                                                        <font-awesome-icon icon="fa-check" />
                                                        Mark as paid
                                                    </button>
                                                    <inertia-link
                                                        v-if="payment.is_pay_button && rented_property.status === 1"
                                                        title="Pay"
                                                        :href="route('rented-properties.pay', [rented_property.id, 'monthly-rent', index])"
                                                        class="hover:bg-blue-200 relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-900">
                                                        <font-awesome-icon icon="fa-dollar-sign" />
                                                        Pay
                                                    </inertia-link>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                </ul>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <pagination class="mt-6" :links="payments.links" :results="payments" :type="'payments'"/>
        </div>
<!--        Month Payments modal-->
        <modal :show="showMonthPaymentsModal" :max-width="'5xl'">
            <div class="rounded-md shadow overflow-x-auto m-2">
                <div
                    class="max-w-3xl mb-5 mx-auto mt-8 md:flex md:items-center md:justify-between md:space-x-5 lg:max-w-7xl">
                    <div>
                        <h1 class="text-2xl font-bold">{{ property.address_1 }}</h1>
                        <p class="text-sm font-medium text-gray-800 underline">
                            <a :href="'https://www.google.com/maps?q='+property.map_address"
                               target="_blank">{{ property.full_address }} <font-awesome-icon :icon="faMapMarkerAlt" class="h-5 w-5 text-gray-800" aria-hidden="true" /></a>
                        </p>
                        <p class="text-xl font-bold text-gray-800 mt-2">
                            {{monthPayments.month_name}} Payments
                        </p>
                        <p class="text-sm font-medium text-gray-800">
                            Rent: ${{monthPayments.rent}}
                        </p>
                        <p class="text-sm font-medium text-gray-800">
                            Late Fees: ${{monthPayments.extra_charges}}
                        </p>
                        <p class="text-sm font-bold text-gray-800">
                            Total: ${{monthPayments.total}}
                        </p>
                    </div>
                </div>
                <div class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="shadow overflow-hidden">
                                <table class="min-w-full divide-y divide-gray-300">
                                    <thead class="bg-blue-50">
                                    <tr>
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Payment Date
                                        </th>
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Amount
                                        </th>
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Late Fees
                                        </th>
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Late Fees Reason
                                        </th>
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Convenience Fee
                                        </th>
<!--                                        <th v-if="$page.props.user.role === 'agent'" scope="col"-->
<!--                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">-->
<!--                                            Monthly Account Fee-->
<!--                                        </th>-->
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            {{$page.props.user.role === 'applicant' ? 'Total' : 'My Payout'}}
                                        </th>
                                        <th scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            {{ $page.props.user.role === 'agent' ? 'Tenant Payment ': '' }}Status
                                        </th>
                                        <th v-if="['local', 'staging'].includes(app_env) && $page.props.user.role === 'applicant'" scope="col"
                                            class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Transfer ID
                                            <small>Staging Only</small>
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="(pay, index) in monthPayments.month_payments" :key="index" class="bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer"
                                    >
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            <span>{{pay.transaction.formatted_paid_date}}</span>
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{ formatCurrency(pay.amount > 0 ? pay.amount : pay.rent) }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            ${{pay.extra_charges}}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{pay.extra_charges_reason}}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            <span>${{$page.props.user.role === 'agent' ? pay.transaction.landlord_stripe_fee : pay.transaction.stripe_fee}}</span>
                                        </td>
<!--                                        <td v-if="$page.props.user.role === 'agent'" class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">-->
<!--                                            <span>{{formatCurrency(pay.transaction.landlord_monthly_stripe_fee)}}</span>-->
<!--                                        </td>-->
                                        <td v-if="$page.props.user.role === 'agent'" class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            <span>{{formatCurrency(pay.transaction.landlord_total)}}</span>
                                        </td>
                                        <td v-else class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            <span>{{formatCurrency(pay.transaction.tenant_total)}}</span>
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            <span>{{ pay.transaction.tenant_payment_status }}</span>
                                        </td>
                                        <td v-if="['local', 'staging'].includes(app_env)" class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            <span>{{$page.props.user.role === 'agent' ? pay.transaction.transfer_id : pay.transaction.payment_intent_id}}</span>
                                        </td>

                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
            <button @click="showMonthPaymentsModal = false" class="bg-white py-2 px-4 m-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 float-right">Close</button>
        </modal>
<!--        One time charges modal-->
        <modal :show="showOneTimeChargesModal" :max-width="'5xl'">
            <div class="rounded-md shadow overflow-x-auto m-2">
                <div
                    class="max-w-3xl mb-5 mx-auto mt-8 md:flex md:items-center md:justify-between md:space-x-5 lg:max-w-7xl">
                    <div>
                        <h1 class="text-2xl font-bold">{{ property.address_1 }}</h1>
                        <p class="text-sm font-medium text-gray-800 underline">
                            <a :href="'https://www.google.com/maps?q='+property.map_address"
                               target="_blank">{{ property.full_address }} <font-awesome-icon :icon="faMapMarkerAlt" class="h-5 w-5 text-gray-800" aria-hidden="true" /></a>
                        </p>
                        <p class="text-sm font-medium text-gray-800 mt-3">
                            Total: ${{oneTimeChargesTotal.amount}}
                        </p>
                        <p class="text-sm font-medium text-gray-800">
                            Paid: ${{oneTimeChargesTotal.paid}}
                        </p>
                        <p class="text-sm text-gray-800">
                            Remaining Amount: ${{oneTimeChargesTotal.remaining}}
                        </p>
                    </div>
                </div>
                <div class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="shadow overflow-hidden">
                                <table class="min-w-full divide-y divide-gray-300">
                                    <thead class="bg-blue-50">
                                        <tr>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                <span @click="sortTable('created_at')">
                                                    Date
                                                    <icon v-show="form.sort_by === 'created_at'" :name="form.sort_direction_icon" />
                                                    <icon v-if="!form.sort_by || form.sort_by !== 'created_at'" :name="'sort'" />
                                                </span>
                                            </th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                Title
                                            </th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                <span @click="sortTable('amount')">
                                                    Amount
                                                    <icon v-show="form.sort_by === 'amount'" :name="form.sort_direction_icon" />
                                                    <icon v-if="!form.sort_by || form.sort_by !== 'amount'" :name="'sort'" />
                                                </span>
                                            </th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                Convenience Fee
                                            </th>
<!--                                            <th v-if="$page.props.user.role === 'agent'" scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">-->
<!--                                                Monthly Account Fee-->
<!--                                            </th>-->
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                {{$page.props.user.role === 'agent' ? 'My Payout' : 'Total'}}
                                            </th>
                                            <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                {{ $page.props.user.role === 'agent' ? 'Tenant Payment ': '' }}Status
                                            </th>
                                            <th v-if="['local', 'staging'].includes(app_env) && $page.props.user.role === 'applicant'" scope="col"
                                                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                Transfer ID
                                                <small>Staging Only</small>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="(transaction, index) in one_time_charges_transactions" :key="transaction.id"
                                        class="bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer"
                                    >
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{ transaction.date }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            <ul v-if="transaction.payments.length > 1" class="mt-3 list-inside text-sm">
                                                <li v-for="(payment, key) in transaction.payments" :key="key">
                                                    {{ payment.month !== null ? payment.month.month_name : payment.one_time_charge.title }}
                                                </li>
                                            </ul>
                                            <span v-else>
                                                {{transaction.payments[0].one_time_charge.title}}
                                            </span>
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            ${{ transaction.amount }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            ${{ transaction.stripe_fee }}
                                        </td>
<!--                                        <td v-if="$page.props.user.role === 'agent'" class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">-->
<!--                                            ${{ transaction.monthly_account_fee ?? '0.00' }}-->
<!--                                        </td>-->
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            ${{ transaction.total }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{ transaction.tenant_payment_status }}
                                        </td>
                                        <td v-if="['local', 'staging'].includes(app_env)" class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            <span>{{$page.props.user.role === 'agent' ? transaction.transfer_id : transaction.payment_intent_id}}</span>
                                        </td>
                                    </tr>
                                    <tr v-show="!one_time_charges_transactions.length">
                                        <td colspan="8" class="px-6 py-4 whitespace-nowrap text-dark-gray-400 text-sm font-medium text-center">
                                            No transactions were found.
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
            <button @click="showOneTimeChargesModal = false" class="bg-white py-2 px-4 m-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 float-right">Close</button>
        </modal>

        <!-- Edit Extra Charges -->
        <modal-jetstream :show="showEditExtraChargesModal" :max-width="'2xl'">
            <div class="rounded-md overflow-x-auto m-2 mb-4">
                <form @submit.prevent="storeExtraCharges" class="space-y-4 p-4">
                    <div class="space-y-2 divide-y divide-gray-200">
                        <div class="mt-6 grid grid-cols-3 gap-y-4 gap-x-4 sm:grid-cols-6">

                            <!-- extra_charges_amount -->
                            <div class="col-span-full">
                                <label for="extra_charges_amount" :class="extraChargesForm.errors.extra_charges_amount ? 'text-red-500' : 'text-gray-700'" class="block text-sm font-medium">
                                    Late Fees for the Month of <span class="font-bold">{{ extraChargesForm.monthName }}</span> <span class="text-red-600">*</span>
                                </label>
                                <div class="mt-1 relative rounded-md">
                                    <div
                                        class="absolute left-0 pl-3 mt-2 pointer-events-none"
                                        v-bind:class="(extraChargesForm.errors.extra_charges_amount)?'':'inset-y-0'">
                                        <font-awesome-icon :icon="faDollar" class="text-gray-400" />
                                    </div>
                                    <input 
                                        type="number" 
                                        step="0.01" 
                                        v-model="extraChargesForm.extra_charges_amount" 
                                        :disabled="extraChargesForm.auto_calculate"
                                        id="extra_charges_amount"
                                        :class="[extraChargesForm.errors.extra_charges_amount ? 'border-red-300' : 'border-gray-300', extraChargesForm.auto_calculate ? 'bg-gray-200' : '']"
                                        class="w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block sm:text-sm border p-2 rounded-md pl-8">
                                        <p v-text="extraChargesForm.errors.extra_charges_amount" class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                            <div class="col-span-full">
                                <label>
                                    <input type="checkbox" v-model="extraChargesForm.auto_calculate"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded mr-1" />
                                    <span class="text-sm">Apply lease-based late fees automatically</span>
                                </label>
                                <p v-text="extraChargesForm.errors.auto_calculate" class="text-red-500 text-sm"></p>
                            </div>
                        </div>
                    </div>

                    <!-- Submit Button -->
                    <div class="text-right pr-2 flex justify-end space-x-2 mb-2">
                        <button @click="showEditExtraChargesModal = false"
                            class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 h-10">
                            Close
                        </button>
                        <form-button :form="extraChargesForm" type="submit" class="h-10 px-4 text-sm">
                            Save
                        </form-button>
                    </div>
                </form>
            </div>
        </modal-jetstream>
        
        <manual-payment-modal
            :show="showManualPaymentModal"
            :rented_property_id="rented_property.id"
            :month="selectedManual?.month"
            :month_name="selectedManual?.month_name"
            :remaining="selectedManual?.remaining || 0"
            :default_amount="selectedManual?.amount || 0"
            :lease_term_complete="rented_property.status === 0"
            :current_lease_term="current_lease_term"
            @close="showManualPaymentModal = false"
            @saved="onManualPaymentSaved"
        />

        <manual-one-time-charge-payment-modal
            :show="showManualOneTimeChargeModal"
            :rented_property_id="rented_property.id"
            :one_time_charge="selectedOneTimeCharge"
            @close="showManualOneTimeChargeModal = false"
            @saved="onManualOneTimeChargeSaved"
        />

    </tailwind-layout>
</template>

<script>
import Icon from '@/Shared/Icon.vue'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination.vue'
import pickBy from 'lodash/pickBy'
import pay from "./Pay.vue";
import Modal from '@/Jetstream/Modal.vue'
import Button from "@/Jetstream/Button.vue";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faChevronLeft, faDollar, faMapMarkerAlt } from '@fortawesome/pro-solid-svg-icons';
import ModalJetstream from "@/Jetstream/Modal.vue";
import FormButton from '@/Shared/FormButton.vue';
import ManualPaymentModal from './ManualPaymentModal.vue';
import ManualOneTimeChargePaymentModal from './ManualOneTimeChargePaymentModal.vue';

export default {
    components: {
        Button,
        Icon,
        Pagination,
        TailwindLayout,
        Modal,
        FontAwesome: FontAwesomeIcon,
        ModalJetstream,
        FormButton,
        ManualPaymentModal,
        ManualOneTimeChargePaymentModal,
    },
    props: {
        filters: Object,
        property: Object,
        rented_property: Object,
        payments: Array,
        one_time_charges: Object,
        one_time_charges_transactions: Object,
        requires_action_transactions: Object,
        app_env: String,
        property_access: {
            type: String,
            default: 'owner',
        },
        current_lease_term: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            faChevronLeft,
            form: {
                search: this.filters.search,
                paginate: (this.filters.paginate) ? this.filters.paginate : 25,
                sort_by: this.filters.sort_by,
                sort_direction_icon: (this.filters.sort_direction === 'desc') ? 'sort-up' : 'sort-down'
            },
            one_time_charges_total: 0,
            showMonthPaymentsModal: false,
            showOneTimeChargesModal: false,
            monthPayments: null,
            showEditExtraChargesModal: false,
            showManualPaymentModal: false,
            selectedManual: null,
            showManualOneTimeChargeModal: false,
            selectedOneTimeCharge: null,

            extraChargesForm: this.$inertia.form({
                rented_property_id: this.rented_property.id,
                month: null,
                extra_charges_amount: null,
                auto_calculate: false,
                monthName: null,
            }),

            isExtraChargesFieldDisabled: false,
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function () {
                this.$inertia.get(this.route('rented-properties.payments', this.rented_property.id), pickBy(this.form), {preserveState: true})
            }, 150),
        },
    },
    computed: {
        oneTimeChargesTotal() {
            const totalCharge = this.one_time_charges.find(charge => charge.title === 'Total');
            return totalCharge;
        },
        isSharedViewOnly() {
            return this.property_access === 'shared_view';
        },
    },
    methods: {
        sortTable(column) {
            if (this.isSharedViewOnly) {
                return;
            }
            this.form.sort_by = column
            this.form.sort_direction = (this.form.sort_direction === 'desc') ? 'asc' : 'desc'
            this.form.sort_direction_icon = (this.form.sort_direction === 'desc') ? 'sort-up' : 'sort-down'
        },
        reset() {
            this.form = mapValues(this.form, () => null)
        },
        formatNumber(value) {
            // Convert to a number and format with two decimal places
            const formattedValue = Number(value).toFixed(2);
            // Add commas as thousands separators
            return formattedValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },
        showMonthPayments(payment) {
            this.monthPayments = payment;
            this.showMonthPaymentsModal = true;
        },
        showOneTimeChargesPayments() {
            this.showOneTimeChargesModal = true;
        },
        formatCurrency(amount) {
            if(!amount) {
                amount = 0;
            }
            amount = parseFloat(amount);
            return amount.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
            })
        },

        editExtraCharges(payment, month) {
            if (this.isSharedViewOnly) {
                return;
            }
            this.extraChargesForm.monthName = payment.month_name;
            this.extraChargesForm.month = month;
            this.extraChargesForm.extra_charges_amount = parseFloat(payment.extra_charges.replace(/,/g, ''));
            this.showEditExtraChargesModal = true;
        },

        storeExtraCharges() {
            if (this.isSharedViewOnly) {
                return;
            }
            this.extraChargesForm.post(this.route('rent-payments.store-extra-charges'), {
                preserveState: true,
                onSuccess: () => {
                    this.showEditExtraChargesModal = false;
                    this.extraChargesForm.reset();
                },
                onError: (errors) => {
                    this.extraChargesForm.errors = errors;
                }
            });
        },

        openManualPaymentModal(payment, month) {
            if (this.isSharedViewOnly) {
                return;
            }
            const remaining = typeof payment.remaining === 'string' ? parseFloat(payment.remaining.replace(/,/g, '')) : parseFloat(payment.remaining);
            this.selectedManual = {
                month: payment.month || month,
                month_name: payment.month_name,
                amount: remaining,
                remaining: remaining,
            };
            this.showManualPaymentModal = true;
        },
        onManualPaymentSaved() {
            this.selectedManual = null;
        },
        openManualOneTimeChargeModal(one_time_charge) {
            if (this.isSharedViewOnly) {
                return;
            }
            this.selectedOneTimeCharge = one_time_charge;
            this.showManualOneTimeChargeModal = true;
        },
        onManualOneTimeChargeSaved() {
            this.selectedOneTimeCharge = null;
        },
    },
}
</script>
